import {
  TAcceptedTypesLiteral,
  TMapSearchParamType,
  TSearchParamsByKey,
} from "src/router/searchParamsTypes"

/**
 * Retrieves and converts a query parameter from the URL's search parameters.
 *
 * @param params.key - The key of the query parameter to retrieve.
 * @param params.type - The expected primitive type of the query parameter.
 * @returns The converted query parameter value or null if not found.
 *
 * @example
 * // For a URL like "https://example.com?userId=123"
 * const userId = getSearchParam({ key: "userId", type: "number" });
 * // Returns: 123 (as a number) or null if not found
 */
export function getSearchParam<const QueryKey extends TSearchParamsByKey>({
  key,
  type,
}: QueryKey): TMapSearchParamType<QueryKey["type"]> | null {
  if (typeof window === "undefined") {
    return null
  }

  const searchParams = new URLSearchParams(window.location.search)

  const searchParam = searchParams.get(key)

  return parseSearchParamValue({
    value: searchParam,
    type,
  }) as TMapSearchParamType<QueryKey["type"]> | null
}

export function parseSearchParamValue({
  value,
  type,
  list,
  allowedValues,
}: {
  value: string | null
  type: TAcceptedTypesLiteral
  list?: boolean
  allowedValues?: string[]
}) {
  if (value === null) {
    return null
  }

  if (!list) {
    return parseValue(value, type, allowedValues)
  }

  if (value === "") {
    return []
  }

  const splittedValues = value.split(",")

  return splittedValues
    .map((val) => parseValue(val, type, allowedValues))
    .filter((val) => val !== null)
}

/**
 * Parse a string value into the specified type.
 *
 * @param value - The string value to parse
 * @param type - The type to parse the value into ('string', 'boolean', or 'number')
 * @param allowedValues - Optional array of allowed string values. Only used when type is 'string'
 * @returns The parsed value of the specified type, or null if parsing fails
 *
 * @example
 * // Parse a string
 * parseValue('hello', 'string') // Returns: 'hello'
 *
 * // Parse a string with allowed values
 * parseValue('foo', 'string', ['foo', 'bar']) // Returns: 'foo'
 * parseValue('baz', 'string', ['foo', 'bar']) // Returns: null
 *
 * // Parse a boolean
 * parseValue('true', 'boolean') // Returns: true
 * parseValue('false', 'boolean') // Returns: false
 *
 * // Parse a number
 * parseValue('123', 'number') // Returns: 123
 * parseValue('abc', 'number') // Returns: null
 */
export function parseValue(
  value: string,
  type: TAcceptedTypesLiteral,
  allowedValues?: string[]
) {
  if (type === "string") {
    const stringifiedValue = String(value)

    if (!allowedValues) {
      return stringifiedValue
    }

    if (allowedValues.includes(stringifiedValue)) {
      return stringifiedValue
    }

    return null
  }

  if (type === "boolean") {
    return value === "true"
  }

  if (type === "number") {
    const parsedNumber = parseInt(value)

    return !isNaN(parsedNumber) ? parsedNumber : null
  }
}
